import { createAsyncThunk } from '@reduxjs/toolkit';
import { DashboardAPI } from '@/api';
import { formatApiError } from '@/utils';
import { IApiError } from '@/models/apiError.model';
import { DASHBOARD_SLICE_NAME } from './models';
import {
  IGetDashboardDataResponse,
  IGetCityStatisticResponse,
  IGetCityStatisticDTO,
  IGetCategoryStatisticDTO,
  IGetCategoryStatisticResponse,
  IGetTrendsResponse,
  IGetBuyersDataResponse,
  IAdminMainMetricsQuery,
  IAdminTopListQuery,
  IGetAdminMainMetricsResponse,
  ITopSupplierRow,
  ITopProductRow,
} from '@/models/dashboard.model';
import { getMockAdminMainMetricsData } from '../helpers';

export const getDashboardData = createAsyncThunk<
  IGetDashboardDataResponse,
  undefined,
  { serializedErrorType: IApiError }
>(
  `${DASHBOARD_SLICE_NAME}/getDashboardData`,
  async () => {
    const response = await DashboardAPI.getDashboardData();
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getCityStatisticData = createAsyncThunk<
  IGetCityStatisticResponse,
  IGetCityStatisticDTO,
  { serializedErrorType: IApiError }
>(
  `${DASHBOARD_SLICE_NAME}/getCityStatisticData`,
  async (data) => {
    const response = await DashboardAPI.getCityStatisticData(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getBuyersData = createAsyncThunk<
  IGetBuyersDataResponse,
  IGetCityStatisticDTO,
  { serializedErrorType: IApiError }
>(
  `${DASHBOARD_SLICE_NAME}/getBuyersData`,
  async (data) => {
    const response = await DashboardAPI.getBuyersData(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getCategoryStatisticData = createAsyncThunk<
  IGetCategoryStatisticResponse,
  IGetCategoryStatisticDTO,
  { serializedErrorType: IApiError }
>(
  `${DASHBOARD_SLICE_NAME}/getCategoryStatisticData`,
  async (data) => {
    const response = await DashboardAPI.getCategoryStatisticData(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getTrends = createAsyncThunk<IGetTrendsResponse, undefined, { serializedErrorType: IApiError }>(
  `${DASHBOARD_SLICE_NAME}/getTrends`,
  async () => {
    const response = await DashboardAPI.getTrends();
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getTopSuppliers = createAsyncThunk<
  { data: ITopSupplierRow[] },
  IAdminTopListQuery,
  { serializedErrorType: IApiError }
>(
  `${DASHBOARD_SLICE_NAME}/getTopSuppliers`,
  async (params) => {
    const response = await DashboardAPI.getTopSuppliers(params);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getTopProducts = createAsyncThunk<
  { data: ITopProductRow[] },
  IAdminTopListQuery,
  { serializedErrorType: IApiError }
>(
  `${DASHBOARD_SLICE_NAME}/getTopProducts`,
  async (params) => {
    const response = await DashboardAPI.getTopProducts(params);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getAdminMainMetricsData = createAsyncThunk<
  IGetAdminMainMetricsResponse,
  IAdminMainMetricsQuery,
  { serializedErrorType: IApiError }
>(
  `${DASHBOARD_SLICE_NAME}/getAdminMainMetricsData`,
  async (params) => {
    try {
      const response = await DashboardAPI.getMainMetrics(params);
      return response.data;
    } catch {
      return { data: getMockAdminMainMetricsData(params) };
    }
  },
  { serializeError: formatApiError },
);
